// slices/videos/videosSlice.ts
import {
  createSlice,
  createEntityAdapter,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import { Video } from '../../types/Video';
import { RootState } from '../../store/store';
import { WatchState } from '../../types/Video';

//──────────────────────────────────────────────
// ⓵  Adapter & helpers
//──────────────────────────────────────────────
const videosAdapter = createEntityAdapter<Video>({
  sortComparer: (a, b) => {
    if (a.ord !== undefined && b.ord !== undefined) return a.ord - b.ord;
    if (a.ord !== undefined) return -1;
    if (b.ord !== undefined) return 1;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  },
});

type VideosStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface LectureVideosState extends ReturnType<
  typeof videosAdapter.getInitialState
> {
  activeVideoId: string | null;
  status: VideosStatus;
  error: string | null;
}

const makeInitialLectureState = (): LectureVideosState =>
  videosAdapter.getInitialState({
    activeVideoId: null,
    status: 'idle',
    error: null,
  });

const setLectureVideos = (
  lectureState: LectureVideosState,
  videos: Video[]
): LectureVideosState => {
  const updated = videosAdapter.setAll(
    lectureState,
    videos
  ) as LectureVideosState | void;

  return updated ?? lectureState;
};

//──────────────────────────────────────────────
// ⓶  Slice state
//──────────────────────────────────────────────
interface VideosState {
  byLectureId: Record<string, LectureVideosState>;
}

const initialState: VideosState = {
  byLectureId: {},
};

//──────────────────────────────────────────────
// ⓷  Utility - choose a sensible active video
//──────────────────────────────────────────────
const pickActiveVideo = (videos: Video[]): string | null => {
  // Prefer videos in progress, then not started, then completed
  const order = ['IN_PROGRESS', 'NOT_STARTED', 'COMPLETED'] as const;
  for (const state of order) {
    const vid = videos.find((v) => v.watchState === state);
    if (vid) return vid.id;
  }
  return videos[0]?.id ?? null;
};

//──────────────────────────────────────────────
// ⓸  Slice
//──────────────────────────────────────────────
const videosSlice = createSlice({
  name: 'videos',
  initialState,
  reducers: {
    videosLoading: (state, { payload }: PayloadAction<string>) => {
      const lectureId = payload;

      const lectureState =
        state.byLectureId[lectureId] ?? makeInitialLectureState();

      lectureState.status = 'loading';
      lectureState.error = null;
      state.byLectureId[lectureId] = lectureState;
    },

    videosLoaded: (
      state,
      { payload }: PayloadAction<{ lectureId: string; videos: Video[] }>
    ) => {
      const { lectureId, videos } = payload;

      let lectureState =
        state.byLectureId[lectureId] ?? makeInitialLectureState();

      lectureState = setLectureVideos(lectureState, videos);
      lectureState.status = 'succeeded';

      // If there's already an active video and it's still valid, keep it
      const currentActiveVideoId = lectureState.activeVideoId;

      const isCurrentActiveVideoStillValid =
        currentActiveVideoId &&
        videos.some((video) => video.id === currentActiveVideoId);

      lectureState.activeVideoId = isCurrentActiveVideoStillValid
        ? currentActiveVideoId
        : pickActiveVideo(videos);

      state.byLectureId[lectureId] = lectureState;
    },

    videosFailed: (state, { payload }: PayloadAction<string>) => {
      const lectureId = payload;

      const lectureState =
        state.byLectureId[lectureId] ?? makeInitialLectureState();

      lectureState.status = 'failed';
      state.byLectureId[lectureId] = lectureState;
    },

    bulkVideosLoading: (state, { payload }: PayloadAction<string[]>) => {
      const lectureIds = payload;

      lectureIds.forEach((lectureId) => {
        const lectureState =
          state.byLectureId[lectureId] ?? makeInitialLectureState();

        lectureState.status = 'loading';
        lectureState.error = null;
        state.byLectureId[lectureId] = lectureState;
      });
    },

    bulkVideosLoaded: (
      state,
      { payload }: PayloadAction<Record<string, Video[]>>
    ) => {
      const videosByLecture = payload;

      Object.entries(videosByLecture).forEach(([lectureId, videos]) => {
        let lectureState =
          state.byLectureId[lectureId] ?? makeInitialLectureState();

        lectureState = setLectureVideos(lectureState, videos);
        lectureState.status = 'succeeded';

        // If there's already an active video and it's still valid, keep it
        const currentActiveVideoId = lectureState.activeVideoId;

        const isCurrentActiveVideoStillValid =
          currentActiveVideoId &&
          videos.some((video) => video.id === currentActiveVideoId);

        lectureState.activeVideoId = isCurrentActiveVideoStillValid
          ? currentActiveVideoId
          : pickActiveVideo(videos);

        state.byLectureId[lectureId] = lectureState;
      });
    },

    videoUpdated: (state, { payload }: PayloadAction<Video>) => {
      const video = payload;
      // Update this video everywhere it appears (session or multiple lectures)
      for (const lectureId in state.byLectureId) {
        const lectureState = state.byLectureId[lectureId];
        if (lectureState.entities[video.id]) {
          videosAdapter.updateOne(lectureState, {
            id: video.id,
            changes: video,
          });
        }
      }
    },

    videoRemoved: (state, { payload }: PayloadAction<string>) => {
      const videoId = payload;
      // Find which lecture contains this video
      for (const lectureId in state.byLectureId) {
        const lectureState = state.byLectureId[lectureId];
        if (lectureState.entities[videoId]) {
          videosAdapter.removeOne(lectureState, videoId);
          break; // Found and removed, no need to continue
        }
      }
    },

    activeVideoSet: (
      state,
      { payload }: PayloadAction<{ lectureId: string; videoId: string | null }>
    ) => {
      const { lectureId, videoId } = payload;

      // Ensure a lecture state exists so we can persist the intent
      const lectureState =
        state.byLectureId[lectureId] ?? makeInitialLectureState();

      lectureState.activeVideoId = videoId;
      state.byLectureId[lectureId] = lectureState;
    },

    clearLectureVideos: (state, { payload }: PayloadAction<string>) => {
      const lectureId = payload;
      delete state.byLectureId[lectureId];
    },

    clearAllVideos: () => initialState,
  },
});

//──────────────────────────────────────────────
// ⓹  Selectors
//──────────────────────────────────────────────
const selectLectureState = (state: RootState, lectureId: string) =>
  state.videos?.byLectureId?.[lectureId] ?? makeInitialLectureState();

// All videos for a lecture, sorted
export const selectVideosByLecture = createSelector(
  [
    (state: RootState, lectureId: string) =>
      selectLectureState(state, lectureId),
  ],
  (lectureState) =>
    videosAdapter
      .getSelectors<LectureVideosState>((ls) => ls)
      .selectAll(lectureState)
);

// Active video
export const selectActiveVideo = createSelector(
  [
    (state: RootState, lectureId: string) =>
      selectLectureState(state, lectureId),
  ],
  (lectureState) =>
    lectureState.activeVideoId
      ? (lectureState.entities[lectureState.activeVideoId] ?? null)
      : null
);

// Videos loading status for a lecture
export const selectVideosLoadingStatus = createSelector(
  [
    (state: RootState, lectureId: string) =>
      selectLectureState(state, lectureId),
  ],
  (lectureState) => lectureState.status
);

// Progress counts
const makeCountSelector = (watchState: Video['watchState']) =>
  createSelector(
    [
      (state: RootState, lectureId: string) =>
        selectVideosByLecture(state, lectureId),
    ],
    (videos) => videos.filter((v) => v.watchState === watchState).length
  );

export const selectCompletedVideosCount = makeCountSelector(
  WatchState.COMPLETED
);
//──────────────────────────────────────────────
// ⓺  Exports
//──────────────────────────────────────────────
export const {
  videosLoading,
  videosLoaded,
  videosFailed,
  bulkVideosLoading,
  bulkVideosLoaded,
  videoUpdated,
  activeVideoSet,
} = videosSlice.actions;

export default videosSlice.reducer;
